import React, { useEffect, useMemo, useState } from 'react';
import {
  Cpu, Save, RefreshCw, Trash2, Plus, ServerCog, ShieldAlert,
  Info, ShieldCheck, Building2, Briefcase, User, Globe,
} from 'lucide-react';
import { Button, Card, EmptyState } from '../index';
import { useAuth } from '../../../contexts/AuthContext';
import { useActiveScope } from '../../../hooks/useActiveScope';
import {
  getAiModelConfigResolved, getAiModelConfigAtScope, upsertAiModelConfig,
  resetAiModelConfig, getOrganizations, getTenantsByOrg, getAiModelConnectorModels,
} from '../../../services/api';

/**
 * AiModelsTab — Admin Center "AI Models" config (Hybrid AutoCreate Slice 4).
 *
 * Per-scope (PLATFORM → ORG → TENANT → AGENT) model registry + capability
 * routing + resilience, binding to /api/v1/admin/ai-model-config. Mirrors the
 * ScoringConfigTab override model: most-specific saved scope wins; a scope with
 * no row inherits. The provider router (backend) returns the managed Claude
 * default whenever a capability has no routing, so this surface is additive —
 * editing it changes nothing until the router is wired into the call sites.
 */

const SCOPES = [
  { key: 'PLATFORM', label: 'Platform', icon: Globe },
  { key: 'ORG', label: 'Org', icon: Building2 },
  { key: 'TENANT', label: 'Tenant', icon: Briefcase },
  { key: 'AGENT', label: 'Agent', icon: User },
];

const CAPABILITIES = [
  { key: 'auto_create_intent', label: 'Auto-Create intent extraction' },
  { key: 'cognitive_inference', label: 'Cognitive agent inference' },
  { key: 'eval_generation', label: 'Eval generation' },
  { key: 'workflow_builder', label: 'Workflow builder' },
  { key: 'embeddings', label: 'Embeddings' },
];

const MODEL_TYPES = [
  { value: 'managed', label: 'Managed (Anthropic Claude)' },
  { value: 'self_hosted_openai', label: 'Self-hosted · OpenAI-compatible' },
  { value: 'self_hosted_anthropic', label: 'Self-hosted · Anthropic-compatible' },
  { value: 'slm', label: 'Small language model (SLM)' },
];

const ON_OVERLOAD = [
  { value: 'retry', label: 'Retry with backoff' },
  { value: 'fallback', label: 'Fall back to secondary' },
  { value: 'fail', label: 'Fail fast' },
];

// Mirrors backend RbacService.ROLE_LEVELS (Platform Admin 6 > Super Admin 5 >
// Org Admin 4 > Tenant Admin 3). Used to gate which scopes a role may open/write.
const ROLE_LEVEL = {
  'Platform Admin': 6, 'Super Admin': 5, 'Org Admin': 4, 'Tenant Admin': 3,
};

const isUUID = (v) => typeof v === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

const emptyEditor = () => ({
  connectedModels: [],
  capabilityRouting: {},
  airGapped: false,
  preferDeterministicStructured: false,
});

export function AiModelsTab() {
  const auth = useAuth();
  // Scope comes from the ACTIVE (pinned) session — NOT auth.user, which never
  // carries orgId/tenantId. Reading user.orgId was the empty-dropdown defect.
  const { orgId: activeOrgId, tenantId: activeTenantId } = useActiveScope();
  // Active role for the pinned scope (session wins over the user-level fallback).
  const role = auth?.session?.role || auth?.user?.role || 'Viewer';
  const myLevel = ROLE_LEVEL[role] || 0;
  const isPlatformAdmin = role === 'Platform Admin';
  const isSuperAdmin = isPlatformAdmin || role === 'Super Admin';
  const userOrgId = activeOrgId;
  const userTenantId = activeTenantId;

  // Mirror AiModelConfigController: PLATFORM → Platform Admin; ORG → Org Admin+;
  // TENANT/AGENT → Tenant Admin+. Row-scope (own org/tenant) is enforced by the
  // backend; the FE only offers the caller's own scope.
  const canWriteScope = (s) => (
    s === 'PLATFORM' ? isPlatformAdmin : s === 'ORG' ? myLevel >= 4 : myLevel >= 3
  );
  // Which scope TABS a role may open at all. PLATFORM is the global/cross-customer
  // default → Platform Admin ONLY. A (customer-scoped) Super Admin manages ORG/
  // TENANT within its own customer, never PLATFORM.
  const canAccessScope = (s) => (
    s === 'PLATFORM' ? isPlatformAdmin : s === 'ORG' ? myLevel >= 4 : myLevel >= 3
  );

  const [scope, setScope] = useState(isPlatformAdmin ? 'PLATFORM' : myLevel >= 4 ? 'ORG' : 'TENANT');
  const [orgId, setOrgId] = useState(activeOrgId);
  const [tenantId, setTenantId] = useState(activeTenantId);
  const [agentId, setAgentId] = useState('');
  const [orgs, setOrgs] = useState([]);
  const [tenants, setTenants] = useState([]);

  const [row, setRow] = useState(null);          // raw config stored AT this scope (null = inherited)
  const [editor, setEditor] = useState(emptyEditor());
  // S3a — the tenant's connected LLM connectors, surfaced as read-only managed
  // models (credential stays in the connector). Available whenever a tenant is
  // in context (TENANT/AGENT scope, or a tenant selected under ORG).
  const [connectorModels, setConnectorModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const flash = (msg, tone = 'info') => { setToast({ msg, tone }); setTimeout(() => setToast(null), 4000); };

  /* Org list (Super Admin sees all; others their own). */
  useEffect(() => {
    (async () => {
      try {
        const res = await getOrganizations();
        const all = Array.isArray(res) ? res : (res?.data || res?.organizations || []);
        setOrgs(isSuperAdmin ? all : all.filter((o) => o.id === userOrgId));
        if (!orgId && (isSuperAdmin ? all.length : userOrgId)) {
          setOrgId(isSuperAdmin ? all[0]?.id : userOrgId);
        }
      } catch (_) { /* selectors degrade */ }
    })();
    /* eslint-disable-next-line */
  }, [isSuperAdmin, userOrgId]);

  /* Tenants for the chosen org. */
  useEffect(() => {
    if (!orgId) { setTenants([]); return; }
    (async () => {
      try {
        const res = await getTenantsByOrg(orgId);
        const all = Array.isArray(res) ? res : (res?.data || res?.tenants || []);
        setTenants(all);
        if (!tenantId && all.length) setTenantId(all[0].id);
      } catch (_) { /* non-fatal */ }
    })();
    /* eslint-disable-next-line */
  }, [orgId]);

  /* Load the at-scope row + the resolved effective config. */
  const load = async () => {
    setLoading(true);
    try {
      const [rowR, effR, connR] = await Promise.all([
        getAiModelConfigAtScope(scope, orgId, tenantId, isUUID(agentId) ? agentId : null).catch(() => null),
        getAiModelConfigResolved(orgId, tenantId, isUUID(agentId) ? agentId : null).catch(() => null),
        // Connector-backed models are tenant-specific → only when the scope targets a tenant.
        ((scope === 'TENANT' || scope === 'AGENT') && isUUID(tenantId))
          ? getAiModelConnectorModels(tenantId).catch(() => null) : Promise.resolve(null),
      ]);
      const connData = connR && connR.ok && Array.isArray(connR.data) ? connR.data : [];
      setConnectorModels(connData);
      // GET at-scope returns 204 (no body) when no row exists → res.ok with null data.
      const rowData = rowR && rowR.ok ? rowR.data : null;
      setRow(rowData || null);
      const src = rowData || (effR && effR.ok ? effR.data : null);
      setEditor(src ? {
        connectedModels: Array.isArray(src.connectedModels) ? src.connectedModels : [],
        capabilityRouting: src.capabilityRouting && typeof src.capabilityRouting === 'object' ? src.capabilityRouting : {},
        airGapped: !!src.airGapped,
        preferDeterministicStructured: !!src.preferDeterministicStructured,
      } : emptyEditor());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, orgId, tenantId, agentId]);

  const scopeReady = scope === 'PLATFORM'
    || (scope === 'ORG' && !!orgId)
    || (scope === 'TENANT' && !!tenantId)
    || (scope === 'AGENT' && !!tenantId && isUUID(agentId));

  const isCustom = !!row;
  const modelOptions = useMemo(() => {
    const seen = new Set();
    const opts = [{ value: '', label: '— Managed default (Claude) —' }];
    // Operator-configured models, then connector-backed models (deduped by id).
    [...editor.connectedModels, ...connectorModels]
      .filter((m) => m && m.id && !seen.has(m.id))
      .forEach((m) => {
        seen.add(m.id);
        opts.push({ value: m.id, label: m.displayName || m.id });
      });
    return opts;
  }, [editor.connectedModels, connectorModels]);

  /* ── Model registry editing ─────────────────────────────────── */
  const addModel = () => setEditor((e) => ({
    ...e,
    connectedModels: [...e.connectedModels, {
      id: `m_${Math.random().toString(36).slice(2, 8)}`,
      displayName: '', type: 'self_hosted_openai', baseUrl: '', modelId: '',
      credentialRef: '', enabled: true,
    }],
  }));
  const updateModel = (idx, patch) => setEditor((e) => ({
    ...e, connectedModels: e.connectedModels.map((m, i) => i === idx ? { ...m, ...patch } : m),
  }));
  const removeModel = (idx) => setEditor((e) => ({
    ...e, connectedModels: e.connectedModels.filter((_, i) => i !== idx),
  }));

  const setRoute = (cap, patch) => setEditor((e) => ({
    ...e, capabilityRouting: { ...e.capabilityRouting, [cap]: { ...(e.capabilityRouting[cap] || {}), ...patch } },
  }));

  /* ── Save / reset ───────────────────────────────────────────── */
  const handleSave = async () => {
    if (!scopeReady) { flash('Pick a complete scope target first.', 'warn'); return; }
    if (!canWriteScope(scope)) {
      flash(scope === 'PLATFORM' ? 'Platform scope requires Platform Admin.' : 'Requires Super Admin.', 'warn');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        scope,
        ...(scope !== 'PLATFORM' && orgId ? { orgId } : {}),
        ...((scope === 'TENANT' || scope === 'AGENT') && tenantId ? { tenantId } : {}),
        ...(scope === 'AGENT' && isUUID(agentId) ? { agentId } : {}),
        connectedModels: editor.connectedModels,
        capabilityRouting: editor.capabilityRouting,
        airGapped: !!editor.airGapped,
        preferDeterministicStructured: !!editor.preferDeterministicStructured,
      };
      const r = await upsertAiModelConfig(payload);
      if (r?.ok === false) flash(`Save failed — ${r.error || 'unknown error'}`, 'error');
      else { flash(`${scope} AI model config saved.`, 'success'); load(); }
    } catch (e) { flash(`Save failed — ${e?.message || e}`, 'error'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!isCustom) { setEditor(emptyEditor()); return; }
    const r = await resetAiModelConfig(scope, orgId, tenantId, isUUID(agentId) ? agentId : null);
    if (r?.ok === false) flash(`Remove failed — ${r.error || 'unknown'}`, 'error');
    else { flash(`${scope} override removed — inheriting from parent scope.`, 'success'); load(); }
  };

  /* ── Render ─────────────────────────────────────────────────── */
  return (
    <div className="space-y-5 px-1 py-3">
      {toast && (
        <div className={`rounded-v2-md p-3 border text-[12.5px] ${
          toast.tone === 'error' ? 'bg-v2-danger-soft border-v2-danger/30 text-v2-danger-strong'
          : toast.tone === 'warn' ? 'bg-v2-warning-soft border-v2-warning/30 text-v2-warning-strong'
          : toast.tone === 'success' ? 'bg-v2-success-soft border-v2-success/30 text-v2-success-strong'
          : 'bg-v2-action-soft border-v2-action/30 text-v2-action-strong'}`}>
          {toast.msg}
        </div>
      )}

      {/* Scope target */}
      <Card padding="md">
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-3">
          Scope · resolves Agent → Tenant → Org → Platform default
        </div>
        <div className="flex gap-1.5 mb-3 flex-wrap">
          {SCOPES.filter((s) => canAccessScope(s.key)).map((s) => (
            <button key={s.key} onClick={() => setScope(s.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-v2-sm text-[12px] font-semibold border transition-colors ${
                scope === s.key ? 'bg-v2-action text-white border-v2-action'
                : 'bg-v2-surface text-v2-ink-secondary border-v2-border-subtle hover:bg-v2-muted'}`}>
              {React.createElement(s.icon, { size: 12 })} {s.label}
            </button>
          ))}
        </div>
        <div className="grid gap-3" style={{
          gridTemplateColumns: scope === 'AGENT' ? 'repeat(3, 1fr)' : scope === 'TENANT' ? 'repeat(2, 1fr)'
            : scope === 'ORG' ? '1fr' : 'none',
        }}>
          {scope !== 'PLATFORM' && (
            <label className="block">
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">Organization</span>
              <select value={orgId || ''} onChange={(e) => setOrgId(e.target.value || null)} disabled={!isSuperAdmin}
                className="w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none disabled:bg-v2-muted">
                {orgs.length === 0 && <option value="">— No orgs —</option>}
                {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
          )}
          {(scope === 'TENANT' || scope === 'AGENT') && (
            <label className="block">
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">Tenant</span>
              <select value={tenantId || ''} onChange={(e) => setTenantId(e.target.value || null)} disabled={!orgId}
                className="w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none disabled:bg-v2-muted">
                {tenants.length === 0 && <option value="">— No tenants —</option>}
                {!tenantId && <option value="">— Select a tenant —</option>}
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          )}
          {scope === 'AGENT' && (
            <label className="block">
              <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">Agent ID (UUID)</span>
              <input value={agentId} onChange={(e) => setAgentId(e.target.value.trim())} placeholder="agent uuid"
                className={`w-full border rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] font-mono-v2 focus:outline-none ${
                  agentId && !isUUID(agentId) ? 'border-v2-danger' : 'border-v2-border-subtle focus:border-v2-action'}`} />
            </label>
          )}
        </div>
      </Card>

      {/* Inherited/custom banner */}
      {!loading && scopeReady && (
        <div className={`rounded-v2-md p-3 border flex items-start gap-2.5 ${
          isCustom ? 'bg-v2-success-soft border-v2-success/30 text-v2-success-strong'
          : 'bg-v2-warning-soft border-v2-warning/30 text-v2-warning-strong'}`}>
          {isCustom ? <ShieldCheck size={16} className="shrink-0 mt-0.5" /> : <Info size={16} className="shrink-0 mt-0.5" />}
          <div className="flex-1 text-[12.5px]">
            <strong>{isCustom ? 'CUSTOM override' : 'Inheriting from parent scope'}.</strong>{' '}
            {isCustom
              ? `This ${scope.toLowerCase()} has its own AI model config.`
              : `No ${scope.toLowerCase()} override — showing inherited values; save to create one.`}
          </div>
          {isCustom && (
            <Button variant="secondary" size="sm" iconLeft={<Trash2 size={13} />} onClick={handleReset} disabled={saving}>
              Remove override
            </Button>
          )}
        </div>
      )}

      {!loading && scopeReady && (
        <>
          {/* Resilience & offline */}
          <Card padding="md">
            <Card.Header title="Resilience & offline behavior"
              subtitle="How this scope degrades when a provider is overloaded or unreachable." />
            <Card.Body>
              <label className="flex items-start gap-2.5 text-[12.5px] text-v2-ink mb-3">
                <input type="checkbox" className="mt-0.5" checked={editor.preferDeterministicStructured}
                  onChange={(e) => setEditor((s) => ({ ...s, preferDeterministicStructured: e.target.checked }))} />
                <span><strong>Prefer deterministic for structured files.</strong> Auto-Create skips the LLM for
                  decision-table xlsx / sectioned docs and extracts intent deterministically (free, instant, offline-safe).</span>
              </label>
              <label className="flex items-start gap-2.5 text-[12.5px] text-v2-ink">
                <input type="checkbox" className="mt-0.5" checked={editor.airGapped}
                  onChange={(e) => setEditor((s) => ({ ...s, airGapped: e.target.checked }))} />
                <span className="flex items-center gap-1.5"><ShieldAlert size={13} className="text-v2-warning" />
                  <span><strong>Air-gapped mode.</strong> Refuse managed (cloud) providers for this scope — only
                    self-hosted / SLM models may serve. Capabilities still routed to a managed model will be rejected.</span></span>
              </label>
            </Card.Body>
          </Card>

          {/* Connected models */}
          <Card padding="md">
            <Card.Header title="Connected models"
              subtitle="Models available at this scope. Secrets are referenced from Secrets Manager — never entered here." />
            <Card.Body>
              {/* S3a — connector-backed models: read-only; the credential stays in
                  the connector, so nothing to enter here. */}
              {connectorModels.length > 0 && (
                <div className="mb-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1.5">
                    From connected providers · credential managed by the connector
                  </div>
                  <div className="space-y-2">
                    {connectorModels.map((m) => (
                      <div key={m.id}
                        className="flex items-center justify-between gap-2 border border-v2-border-subtle rounded-v2-md px-3 py-2 bg-v2-muted">
                        <div className="min-w-0">
                          <div className="text-[12.5px] font-medium text-v2-ink truncate">{m.displayName || m.modelId || m.id}</div>
                          <div className="text-[11px] text-v2-ink-muted truncate">
                            {m.provider || 'managed'}{m.modelId ? ` · ${m.modelId}` : ''}{m.connectorName ? ` · via ${m.connectorName}` : ''}
                          </div>
                        </div>
                        <span className="flex items-center gap-1 text-[10.5px] font-semibold text-v2-success-strong bg-v2-success-soft rounded-v2-pill px-2 py-0.5 shrink-0">
                          <ShieldCheck size={11} /> Connected
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {editor.connectedModels.length === 0 && connectorModels.length === 0 && (
                <EmptyState icon={<ServerCog size={20} />} title="No models connected at this scope"
                  description="Connect an LLM provider under Connectors (e.g. Anthropic) to surface it here, add a self-hosted / SLM endpoint below, or leave empty to use the managed Claude default." />
              )}
              <div className="space-y-3">
                {editor.connectedModels.map((m, idx) => (
                  <div key={m.id || idx} className="border border-v2-border-subtle rounded-v2-md p-3 bg-v2-surface">
                    <div className="grid gap-2.5" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                      <Field label="Display name">
                        <input value={m.displayName || ''} onChange={(e) => updateModel(idx, { displayName: e.target.value })}
                          className={inputCls} placeholder="On-prem Llama 3.1" />
                      </Field>
                      <Field label="Type">
                        <select value={m.type || 'self_hosted_openai'} onChange={(e) => updateModel(idx, { type: e.target.value })} className={inputCls}>
                          {MODEL_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </Field>
                      <Field label="Base URL">
                        <input value={m.baseUrl || ''} onChange={(e) => updateModel(idx, { baseUrl: e.target.value })}
                          className={inputCls} placeholder="https://vllm.internal/v1" disabled={m.type === 'managed'} />
                      </Field>
                      <Field label="Model ID">
                        <input value={m.modelId || ''} onChange={(e) => updateModel(idx, { modelId: e.target.value })}
                          className={inputCls} placeholder="llama-3.1-70b-instruct" />
                      </Field>
                      <Field label="Credential (Secrets Manager ref)">
                        <input value={m.credentialRef || ''} onChange={(e) => updateModel(idx, { credentialRef: e.target.value })}
                          className={`${inputCls} font-mono-v2`} placeholder="sm://ai/llama-key" disabled={m.type === 'managed'} />
                      </Field>
                      <div className="flex items-end justify-between">
                        <label className="flex items-center gap-2 text-[12px] text-v2-ink">
                          <input type="checkbox" checked={m.enabled !== false} onChange={(e) => updateModel(idx, { enabled: e.target.checked })} />
                          Enabled
                        </label>
                        <Button variant="ghost" size="sm" iconLeft={<Trash2 size={13} />} onClick={() => removeModel(idx)}>Remove</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <Button variant="secondary" size="sm" iconLeft={<Plus size={13} />} onClick={addModel}>Add model</Button>
              </div>
            </Card.Body>
          </Card>

          {/* Capability routing */}
          <Card padding="md">
            <Card.Header title="Capability → model routing & cost controls"
              subtitle="Route each AI task to a primary model with a fallback, and cap tokens / set temperature per task. Blank = managed Claude default (today's behaviour)." />
            <Card.Body>
              <div className="space-y-2.5">
                {CAPABILITIES.map((cap) => {
                  const r = editor.capabilityRouting[cap.key] || {};
                  return (
                    <div key={cap.key} className="grid gap-2.5 items-center"
                      style={{ gridTemplateColumns: '1.4fr 1fr 1fr 0.9fr 0.7fr 0.7fr' }}>
                      <div className="text-[12.5px] font-medium text-v2-ink">{cap.label}</div>
                      <select value={r.primaryModelId || ''} onChange={(e) => setRoute(cap.key, { primaryModelId: e.target.value || null })} className={inputCls}>
                        {modelOptions.map((o) => <option key={`p-${o.value}`} value={o.value}>{o.label}</option>)}
                      </select>
                      <select value={r.fallbackModelId || ''} onChange={(e) => setRoute(cap.key, { fallbackModelId: e.target.value || null })} className={inputCls}>
                        {modelOptions.map((o) => <option key={`f-${o.value}`} value={o.value}>{o.label === '— Managed default (Claude) —' ? '— No fallback —' : o.label}</option>)}
                      </select>
                      <select value={r.onOverload || 'retry'} onChange={(e) => setRoute(cap.key, { onOverload: e.target.value })} className={inputCls}>
                        {ON_OVERLOAD.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <input type="number" min="256" step="256" value={r.maxTokens ?? ''}
                        onChange={(e) => setRoute(cap.key, { maxTokens: e.target.value === '' ? null : (parseInt(e.target.value, 10) > 0 ? parseInt(e.target.value, 10) : null) })}
                        className={inputCls} placeholder="8192" title="Max output tokens (blank = default)" />
                      <input type="number" min="0" max="1" step="0.1" value={r.temperature ?? ''}
                        onChange={(e) => setRoute(cap.key, { temperature: e.target.value === '' ? null : parseFloat(e.target.value) })}
                        className={inputCls} placeholder="—" title="Temperature 0–1 (blank = provider default)" />
                    </div>
                  );
                })}
                <div className="grid gap-2.5 text-[10px] font-semibold uppercase tracking-wider text-v2-ink-muted"
                  style={{ gridTemplateColumns: '1.4fr 1fr 1fr 0.9fr 0.7fr 0.7fr' }}>
                  <div>Capability</div><div>Primary</div><div>Fallback</div><div>On overload</div><div>Max tokens</div><div>Temp</div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Save bar */}
          <div className="flex items-center gap-2">
            <Button variant="primary" iconLeft={<Save size={13} />} onClick={handleSave}
              disabled={saving || !canWriteScope(scope)}>
              {saving ? 'Saving…' : isCustom ? 'Update override' : 'Create override'}
            </Button>
            <Button variant="secondary" iconLeft={<RefreshCw size={13} />} onClick={handleReset} disabled={saving}>
              {isCustom ? 'Remove override' : 'Clear editor'}
            </Button>
            {!canWriteScope(scope) && (
              <span className="text-[11px] text-v2-warning">
                {scope === 'PLATFORM' ? 'Platform scope requires Platform Admin.' : 'Requires Super Admin.'}
              </span>
            )}
          </div>
        </>
      )}

      {!loading && !scopeReady && (
        <EmptyState icon={<Cpu size={20} />} title="Select a scope target"
          description="Pick the organization, tenant, or agent this configuration applies to." />
      )}
    </div>
  );
}

const inputCls = 'w-full border border-v2-border-subtle rounded-v2-sm px-2 py-1.5 bg-v2-surface text-[12.5px] focus:border-v2-action focus:outline-none disabled:bg-v2-muted disabled:text-v2-ink-muted';

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">{label}</span>
      {children}
    </label>
  );
}

export default AiModelsTab;
